import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ProviderPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/breadcrumbs";

export default function ShopPage() {
  const [providers, setProviders] = useState<ProviderPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getShopProviders()
      .then((r) => setProviders(r.providers))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"));
  }, []);

  if (error) {
    return (
      <div data-page="shop" data-state="error" className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold">Shop</h1>
        <p className="text-sm text-destructive" data-error>{error}</p>
      </div>
    );
  }

  if (providers === null) {
    return (
      <div data-page="shop" data-state="loading">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div data-page="shop" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Events", to: "/discover" }, { label: "Shop" }]} />
      <div>
        <h1 className="text-2xl font-semibold">Shop</h1>
        <p className="text-muted-foreground">Group deals negotiated for Dugads families. Providers invoice and deliver directly -- no payment through Dugads.</p>
      </div>

      {providers.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">No shops are available yet.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-region="provider-tiles">
          {providers.map((p) => (
            <Card key={p.id} data-item="provider" data-item-id={p.id} className="h-full">
              <CardContent className="flex h-full flex-col gap-3 p-4">
                {p.logo_url && <img src={p.logo_url} alt={`${p.name} logo`} className="h-12 w-auto object-contain" />}
                <div>
                  <h2 className="text-lg font-semibold">{p.name}</h2>
                  {p.discount_blurb && <p className="text-xs text-muted-foreground">{p.discount_blurb}</p>}
                </div>
                {p.description && <p className="text-sm text-muted-foreground line-clamp-3">{p.description}</p>}
                <Link
                  to={`/shop/${p.slug}`}
                  className={`${buttonVariants({ size: "sm" })} mt-auto`}
                  data-action="browse-provider"
                >
                  {p.fulfillment_type === "external_redirect"
                    ? (p.external_cta_label || `Shop ${p.name}`)
                    : p.fulfillment_type === "quote"
                    ? `Get a quote`
                    : p.fulfillment_type === "cashback"
                    ? `Earn cashback`
                    : `Browse ${p.name}`}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
